import * as i1 from '@angular/common';
import { NgClass, NgStyle, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, Injectable, Pipe, Component, Input, input, computed, ChangeDetectionStrategy, NgModule } from '@angular/core';
import { SkyThemeIconManifestService, SkyThemeService } from '@skyux/theme';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { switchMap, catchError, of } from 'rxjs';

/**
 * @internal
 */
class SkyIconResolverService {
    #glyphMap = new Map();
    #manifestSvc = inject(SkyThemeIconManifestService);
    constructor() {
        // Map the icons by name for more efficient lookup.
        for (const glyph of this.#manifestSvc.getManifest().glyphs) {
            // TODO: keep this until `faName` is removed just in case any icons get added with the wrong shape
            if (glyph.faName && !glyph.faNames) {
                glyph.faNames = [glyph.faName];
            }
            this.#glyphMap.set(glyph.name, glyph);
        }
    }
    resolveIcon(icon, variant, themeSettings) {
        let iconType = 'fa';
        const variantIcon = variant && `${icon}-${variant}`;
        const lineIcon = `${icon}-line`;
        // Get the specified variant, or fall back to the icon name.
        // If the pure icon name doesn't exist, try the line variant.
        let glyph = this.#glyphMap.get(variantIcon) ??
            this.#glyphMap.get(icon) ??
            this.#glyphMap.get(lineIcon) ??
            Array.from(this.#glyphMap.values()).find((glyph) => glyph.aliases?.includes(variantIcon) ||
                glyph.aliases?.includes(lineIcon) ||
                glyph.aliases?.includes(icon));
        // If still no icon is found, search through the icons that match the FA name.
        if (!glyph) {
            let glyphs = Array.from(this.#glyphMap.values()).filter((g) => g.faNames?.includes(icon));
            if (glyphs.length) {
                if (glyphs.length > 1) {
                    // If multiples are found, make sure we select the correct variant, if requested.
                    // The icons are ordered such that the "default" variant will be the first one.
                    if (variant) {
                        const variantGlyph = glyphs.find((g) => g.name.endsWith(variant));
                        if (variantGlyph) {
                            glyphs = [variantGlyph];
                        }
                    }
                }
                glyph = glyphs[0];
            }
            // If none of the above works, no matching icon was found
        }
        if (glyph) {
            // If a glyph is found, use it for modern theme or if no FA fallback exists.
            if (themeSettings?.theme.name === 'modern' || !glyph.faNames?.length) {
                icon = glyph.name;
                iconType = 'skyux';
            }
            else {
                // For default theme, use a known FA fallback.
                // If the icon name requested is already a FA icon, just use it. Otherwise use the first one.
                if (!glyph.faNames?.includes(icon)) {
                    icon = glyph.faNames[0];
                }
            }
        }
        // If no SKY UX glyph is found, assume icon is part of FA and pass it along.
        return {
            icon,
            iconType,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconResolverService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconResolverService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconResolverService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

/**
 * @internal
 */
class SkyIconClassListPipe {
    #resolver = inject(SkyIconResolverService);
    transform(icon, themeSettings, size, fixedWidth, variant) {
        let classList;
        const { icon: resolvedIcon, iconType: resolvedIconType } = this.#resolver.resolveIcon(icon, variant, themeSettings);
        if (resolvedIconType === 'skyux') {
            classList = ['sky-i-' + resolvedIcon];
        }
        else {
            classList = ['fa', 'fa-' + resolvedIcon];
        }
        if (size) {
            classList.push('fa-' + size);
        }
        if (fixedWidth) {
            classList.push('fa-fw');
        }
        return classList;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconClassListPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: SkyIconClassListPipe, name: "skyIconClassList" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconClassListPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'skyIconClassList',
                }]
        }] });

/**
 * @internal
 */
class SkyIconStackComponent {
    constructor() {
        this.themeSvc = inject(SkyThemeService, { optional: true });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconStackComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.4", type: SkyIconStackComponent, selector: "sky-icon-stack", inputs: { size: "size", baseIcon: "baseIcon", topIcon: "topIcon" }, ngImport: i0, template: "<span\n  aria-hidden=\"true\"\n  class=\"sky-icon-stack fa-stack\"\n  [ngClass]=\"size ? 'fa-' + size + ' sky-icon-stack-size-' + size : ''\"\n>\n  <i\n    *ngIf=\"baseIcon\"\n    class=\"sky-icon fa-stack-2x\"\n    [ngClass]=\"\n      baseIcon.icon\n        | skyIconClassList: (themeSvc?.settingsChange | async)?.currentSettings\n    \"\n  ></i>\n  <i\n    *ngIf=\"topIcon\"\n    class=\"sky-icon fa-stack-1x fa-inverse sky-icon-inverse\"\n    [ngClass]=\"\n      topIcon.icon\n        | skyIconClassList: (themeSvc?.settingsChange | async)?.currentSettings\n    \"\n  ></i>\n</span>\n", styles: [":host{display:inline-block;vertical-align:bottom}.sky-icon-stack-size-xs.fa-stack{width:1.25em;height:1.25em;line-height:1.25em}.sky-icon-stack-size-xs .fa-stack-2x{font-size:1.25em}.sky-icon-stack-size-xs .fa-stack-1x{font-size:.625em}.fa-stack-1x.sky-i-check:before{position:relative;top:.5px}.fa-stack-1x.sky-i-exclamation:before{position:relative;top:1.5px}.sky-icon-inverse{color:var(--sky-icon-stack-top-icon-color-override, #ffffff)}:host-context(.sky-theme-modern.sky-theme-mode-dark) .sky-icon-inverse{color:#121212}.sky-theme-modern.sky-theme-mode-dark .sky-icon-inverse{color:#121212}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: SkyIconClassListPipe, name: "skyIconClassList" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconStackComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sky-icon-stack', template: "<span\n  aria-hidden=\"true\"\n  class=\"sky-icon-stack fa-stack\"\n  [ngClass]=\"size ? 'fa-' + size + ' sky-icon-stack-size-' + size : ''\"\n>\n  <i\n    *ngIf=\"baseIcon\"\n    class=\"sky-icon fa-stack-2x\"\n    [ngClass]=\"\n      baseIcon.icon\n        | skyIconClassList: (themeSvc?.settingsChange | async)?.currentSettings\n    \"\n  ></i>\n  <i\n    *ngIf=\"topIcon\"\n    class=\"sky-icon fa-stack-1x fa-inverse sky-icon-inverse\"\n    [ngClass]=\"\n      topIcon.icon\n        | skyIconClassList: (themeSvc?.settingsChange | async)?.currentSettings\n    \"\n  ></i>\n</span>\n", styles: [":host{display:inline-block;vertical-align:bottom}.sky-icon-stack-size-xs.fa-stack{width:1.25em;height:1.25em;line-height:1.25em}.sky-icon-stack-size-xs .fa-stack-2x{font-size:1.25em}.sky-icon-stack-size-xs .fa-stack-1x{font-size:.625em}.fa-stack-1x.sky-i-check:before{position:relative;top:.5px}.fa-stack-1x.sky-i-exclamation:before{position:relative;top:1.5px}.sky-icon-inverse{color:var(--sky-icon-stack-top-icon-color-override, #ffffff)}:host-context(.sky-theme-modern.sky-theme-mode-dark) .sky-icon-inverse{color:#121212}.sky-theme-modern.sky-theme-mode-dark .sky-icon-inverse{color:#121212}\n"] }]
        }], propDecorators: { size: [{
                type: Input
            }], baseIcon: [{
                type: Input
            }], topIcon: [{
                type: Input
            }] } });

async function getIconMap() {
    const response = await fetch(`https://sky.blackbaudcdn.net/static/skyux-icons/7/assets/svg/skyux-icons.svg`);
    if (!response.ok) {
        throw new Error('Icon sprite could not be loaded.');
    }
    const markup = await response.text();
    document.body.insertAdjacentHTML('afterbegin', markup);
    const iconMap = Array.from(document.querySelectorAll('#sky-icon-svg-sprite symbol')).reduce((map, el) => {
        const idParts = el.id.split('-');
        // Construct the icon name by removing `sky-i-` from the beginning
        // and `-<size>-<variant>` from the end.
        const name = idParts.slice(2, idParts.length - 2).join('-');
        let sizes = map.get(name);
        if (!sizes) {
            sizes = [];
            map.set(name, sizes);
        }
        // The penultimate segment is the size for which the icon has
        // been optimized.
        sizes.push(+idParts[idParts.length - 2]);
        return map;
    }, new Map());
    // Sort all the sizes for later comparison.
    for (const id of iconMap.keys()) {
        // Dedupe and sort the icon sizes.
        iconMap.set(id, [...new Set(iconMap.get(id))].sort());
    }
    return iconMap;
}
function getNearestSize(iconMap, name, pixelSize) {
    const sizes = iconMap.get(name);
    if (sizes) {
        let nearestSizeUnder = -Infinity;
        let nearestSizeOver = Infinity;
        for (const availableSize of sizes) {
            if (availableSize === pixelSize) {
                return pixelSize;
            }
            else if (availableSize < pixelSize) {
                nearestSizeUnder = availableSize;
            }
            else {
                nearestSizeOver = availableSize;
                break;
            }
        }
        const underDiff = Math.abs(pixelSize - nearestSizeUnder);
        const overDiff = Math.abs(pixelSize - nearestSizeOver);
        return isNaN(overDiff) || underDiff < overDiff
            ? nearestSizeUnder
            : nearestSizeOver;
    }
    return undefined;
}
/**
 * @internal
 */
class SkyIconSvgResolverService {
    #iconMapPromise;
    async resolveHref(name, pixelSize = 16, variant = 'line') {
        if (!this.#iconMapPromise) {
            this.#iconMapPromise = getIconMap();
        }
        const iconMap = await this.#iconMapPromise;
        let href = `#sky-i-${name}`;
        // Find the icon with the optimal size nearest to the requested size.
        const nearestSize = getNearestSize(iconMap, name, pixelSize);
        if (!nearestSize) {
            throw new Error(`Icon with name '${name}' was not found.`);
        }
        href = `${href}-${nearestSize}-${variant}`;
        return href;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconSvgResolverService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconSvgResolverService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconSvgResolverService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const SIZE_BASE = 16;
const SIZES = new Map([
    ['', SIZE_BASE],
    ['lg', 21.333 /* SIZE_BASE * (4/3) */],
    ['2x', 32 /* SIZE_BASE * 2 */],
    ['3x', 48 /* SIZE_BASE * 3 */],
    ['4x', 64 /* SIZE_BASE * 4 */],
    ['5x', 80 /* SIZE_BASE * 5 */],
]);
/**
 * @internal
 */
class SkyIconSvgComponent {
    constructor() {
        this.#resolverSvc = inject(SkyIconSvgResolverService);
        this.iconName = input.required();
        this.iconSize = input();
        this.iconVariant = input();
        this.#iconInfo = computed(() => {
            return {
                src: this.iconName(),
                size: this.iconSize(),
                variant: this.iconVariant(),
            };
        });
        this.iconHref = toSignal(toObservable(this.#iconInfo).pipe(switchMap((info) => this.#resolverSvc.resolveHref(info.src, SIZES.get(info.size ?? ''), info.variant)), catchError(() => of(''))));
    }
    #resolverSvc;
    #iconInfo;
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconSvgComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "17.3.4", type: SkyIconSvgComponent, isStandalone: true, selector: "sky-icon-svg", inputs: { iconName: { classPropertyName: "iconName", publicName: "iconName", isSignal: true, isRequired: true, transformFunction: null }, iconSize: { classPropertyName: "iconSize", publicName: "iconSize", isSignal: true, isRequired: false, transformFunction: null }, iconVariant: { classPropertyName: "iconVariant", publicName: "iconVariant", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0, template: "<svg\n  aria-hidden=\"true\"\n  class=\"sky-icon-svg-img\"\n  [ngClass]=\"iconSize() && 'sky-icon-svg-img-' + iconSize()\"\n>\n  <use [attr.xlink:href]=\"iconHref()\"></use>\n</svg>\n", styles: [":host{display:inline-block;--sky-icon-svg-img-size: 1em;--sky-icon-svg-img-top: .15em}.sky-icon-svg-img{display:inline-block;fill:var(--sky-icon-svg-color-input, currentColor);height:var(--sky-icon-svg-img-size);width:var(--sky-icon-svg-img-size);text-align:center;position:relative;top:var(--sky-icon-svg-img-top)}.sky-icon-svg-img-lg{--sky-icon-svg-img-size: 1.333em;--sky-icon-svg-img-top: .2em}.sky-icon-svg-img-2x{--sky-icon-svg-img-size: 2em;--sky-icon-svg-img-top: .3em}.sky-icon-svg-img-3x{--sky-icon-svg-img-size: 3em;--sky-icon-svg-img-top: .45em}.sky-icon-svg-img-4x{--sky-icon-svg-img-size: 4em;--sky-icon-svg-img-top: .6em}.sky-icon-svg-img-5x{--sky-icon-svg-img-size: 5em;--sky-icon-svg-img-top: .75em}\n", ":host{display:inline-block;--sky-icon-svg-img-size: 1em;--sky-icon-svg-img-top: .15em}.sky-icon-svg-img{display:inline-block;fill:var(--sky-icon-svg-color-input, currentColor);height:var(--sky-icon-svg-img-size);width:var(--sky-icon-svg-img-size);text-align:center;position:relative;top:var(--sky-icon-svg-img-top)}.sky-icon-svg-img-lg{--sky-icon-svg-img-size: 1.333em;--sky-icon-svg-img-top: .2em}.sky-icon-svg-img-2x{--sky-icon-svg-img-size: 2em;--sky-icon-svg-img-top: .3em}.sky-icon-svg-img-3x{--sky-icon-svg-img-size: 3em;--sky-icon-svg-img-top: .45em}.sky-icon-svg-img-4x{--sky-icon-svg-img-size: 4em;--sky-icon-svg-img-top: .6em}.sky-icon-svg-img-5x{--sky-icon-svg-img-size: 5em;--sky-icon-svg-img-top: .75em}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconSvgComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sky-icon-svg', standalone: true, imports: [NgClass, NgStyle], template: "<svg\n  aria-hidden=\"true\"\n  class=\"sky-icon-svg-img\"\n  [ngClass]=\"iconSize() && 'sky-icon-svg-img-' + iconSize()\"\n>\n  <use [attr.xlink:href]=\"iconHref()\"></use>\n</svg>\n", styles: [":host{display:inline-block;--sky-icon-svg-img-size: 1em;--sky-icon-svg-img-top: .15em}.sky-icon-svg-img{display:inline-block;fill:var(--sky-icon-svg-color-input, currentColor);height:var(--sky-icon-svg-img-size);width:var(--sky-icon-svg-img-size);text-align:center;position:relative;top:var(--sky-icon-svg-img-top)}.sky-icon-svg-img-lg{--sky-icon-svg-img-size: 1.333em;--sky-icon-svg-img-top: .2em}.sky-icon-svg-img-2x{--sky-icon-svg-img-size: 2em;--sky-icon-svg-img-top: .3em}.sky-icon-svg-img-3x{--sky-icon-svg-img-size: 3em;--sky-icon-svg-img-top: .45em}.sky-icon-svg-img-4x{--sky-icon-svg-img-size: 4em;--sky-icon-svg-img-top: .6em}.sky-icon-svg-img-5x{--sky-icon-svg-img-size: 5em;--sky-icon-svg-img-top: .75em}\n", ":host{display:inline-block;--sky-icon-svg-img-size: 1em;--sky-icon-svg-img-top: .15em}.sky-icon-svg-img{display:inline-block;fill:var(--sky-icon-svg-color-input, currentColor);height:var(--sky-icon-svg-img-size);width:var(--sky-icon-svg-img-size);text-align:center;position:relative;top:var(--sky-icon-svg-img-top)}.sky-icon-svg-img-lg{--sky-icon-svg-img-size: 1.333em;--sky-icon-svg-img-top: .2em}.sky-icon-svg-img-2x{--sky-icon-svg-img-size: 2em;--sky-icon-svg-img-top: .3em}.sky-icon-svg-img-3x{--sky-icon-svg-img-size: 3em;--sky-icon-svg-img-top: .45em}.sky-icon-svg-img-4x{--sky-icon-svg-img-size: 4em;--sky-icon-svg-img-top: .6em}.sky-icon-svg-img-5x{--sky-icon-svg-img-size: 5em;--sky-icon-svg-img-top: .75em}\n"] }]
        }] });

class SkyIconComponent {
    constructor() {
        this.themeSvc = inject(SkyThemeService, { optional: true });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.4", type: SkyIconComponent, selector: "sky-icon", inputs: { icon: "icon", iconName: "iconName", iconType: "iconType", size: "size", fixedWidth: "fixedWidth", variant: "variant" }, ngImport: i0, template: "@if (iconName) {\n  <sky-icon-svg [iconName]=\"iconName\" [iconSize]=\"size\" />\n} @else {\n  <i\n    *ngIf=\"icon\"\n    aria-hidden=\"true\"\n    class=\"sky-icon\"\n    [attr.data-sky-icon]=\"icon\"\n    [attr.data-sky-icon-type]=\"iconType\"\n    [attr.data-sky-icon-variant]=\"variant\"\n    [ngClass]=\"\n      icon\n        | skyIconClassList\n          : (themeSvc?.settingsChange | async)?.currentSettings\n          : size\n          : fixedWidth\n          : variant\n    \"\n  ></i>\n}\n", styles: [":host{display:inline-block}i{display:inherit}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SkyIconSvgComponent, selector: "sky-icon-svg", inputs: ["iconName", "iconSize", "iconVariant"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: SkyIconClassListPipe, name: "skyIconClassList" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sky-icon', changeDetection: ChangeDetectionStrategy.OnPush, template: "@if (iconName) {\n  <sky-icon-svg [iconName]=\"iconName\" [iconSize]=\"size\" />\n} @else {\n  <i\n    *ngIf=\"icon\"\n    aria-hidden=\"true\"\n    class=\"sky-icon\"\n    [attr.data-sky-icon]=\"icon\"\n    [attr.data-sky-icon-type]=\"iconType\"\n    [attr.data-sky-icon-variant]=\"variant\"\n    [ngClass]=\"\n      icon\n        | skyIconClassList\n          : (themeSvc?.settingsChange | async)?.currentSettings\n          : size\n          : fixedWidth\n          : variant\n    \"\n  ></i>\n}\n", styles: [":host{display:inline-block}i{display:inherit}\n"] }]
        }], propDecorators: { icon: [{
                type: Input
            }], iconName: [{
                type: Input
            }], iconType: [{
                type: Input
            }], size: [{
                type: Input
            }], fixedWidth: [{
                type: Input
            }], variant: [{
                type: Input
            }] } });

class SkyIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: SkyIconModule, declarations: [SkyIconClassListPipe, SkyIconComponent, SkyIconStackComponent], imports: [CommonModule, SkyIconSvgComponent], exports: [SkyIconComponent, SkyIconStackComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: SkyIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SkyIconClassListPipe, SkyIconComponent, SkyIconStackComponent],
                    imports: [CommonModule, SkyIconSvgComponent],
                    exports: [SkyIconComponent, SkyIconStackComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SkyIconModule, SkyIconComponent as λ1, SkyIconStackComponent as λ2 };
